import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, Phone, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ModalShell } from '@/components/ModalShell';
import { useRecentCompleted, type RecentCompleted } from '@/features/agent-metrics/useRecentCompleted';
import { useScreeningReview } from '@/features/screening/screeningReview';
import { formatPhone, formatRelative } from '@/lib/format';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * F5-R1a (encargo 4) — «Completed screenings» al brand Y por i18n ES/EN
 * (estaba clavado en inglés). Misma lógica, mismos datos, mismo review modal;
 * solo el vestido (paleta nav-*) y las etiquetas por t().
 */
interface CompletedListModalProps {
  open: boolean;
  onClose: () => void;
}

type Filter = 'all' | 'needs_nav' | 'no_findings';

export function CompletedListModal({ open, onClose }: CompletedListModalProps): JSX.Element | null {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const completedQ = useRecentCompleted(100);
  const [filter, setFilter] = useState<Filter>('all');
  const [reviewId, setReviewId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <>
      <ModalShell open={open} onClose={onClose} ariaLabelledBy="completed-modal-title">
        <header className="sticky top-0 bg-white border-b border-nav-hair px-6 py-5 flex items-center justify-between rounded-t-[14px] z-10">
          <h2 id="completed-modal-title" className="font-head text-xl font-bold text-nav-navy tracking-tight">
            {t('agentF5.r1a.cl.title')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('common.close')}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="modal-scroll flex-1 overflow-y-auto font-body text-nav-navy">
          <div
            ref={dialogRef}
            tabIndex={-1}
            className="px-6 py-4"
          >
            <div className="border-b border-nav-hair pb-4 mb-4">
              <FilterTabs value={filter} onChange={setFilter} />
            </div>

            {completedQ.isLoading && (
              <div className="text-center py-8">
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-nav-teal" />
              </div>
            )}

            {completedQ.error && (
              <Alert variant="error">
                <AlertTitle>{t('agentF5.r1a.cl.errLoad')}</AlertTitle>
                <AlertDescription>{(completedQ.error as Error).message}</AlertDescription>
              </Alert>
            )}

            {completedQ.data && (
              <CompletedRows
                rows={completedQ.data}
                filter={filter}
                onSelect={(id) => setReviewId(id)}
              />
            )}
          </div>
        </div>
      </ModalShell>

      {reviewId && (
        <ScreeningReviewModal screeningId={reviewId} onClose={() => setReviewId(null)} />
      )}
    </>
  );
}

function FilterTabs({
  value,
  onChange,
}: {
  value: Filter;
  onChange: (v: Filter) => void;
}): JSX.Element {
  const t = useT();
  // F5-R1a — clases ESTÁTICAS del brand (las `bg-${tone}-100` dinámicas de
  // antes ni siquiera compilaban en Tailwind) + etiquetas por i18n.
  const tabs: Array<{ key: Filter; label: string; activeCls: string }> = [
    { key: 'all', label: t('agentF5.r1a.cl.tabAll'), activeCls: 'bg-nav-tealTint text-nav-tealDark' },
    { key: 'needs_nav', label: t('agentF5.r1a.cl.tabNeedsNav'), activeCls: 'bg-nav-amberTint text-nav-amberText' },
    { key: 'no_findings', label: t('agentF5.r1a.cl.tabNoFindings'), activeCls: 'bg-nav-sageTint text-nav-sageText' },
  ];
  return (
    <div role="tablist" className="inline-flex rounded-[10px] border-[1.5px] border-nav-card bg-white overflow-hidden">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={value === tab.key}
          onClick={() => onChange(tab.key)}
          className={cn(
            'px-3 min-h-[44px] font-head text-sm font-semibold transition-colors',
            value === tab.key ? tab.activeCls : 'text-nav-slate hover:bg-nav-soft',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function CompletedRows({
  rows,
  filter,
  onSelect,
}: {
  rows: RecentCompleted[];
  filter: Filter;
  onSelect: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'needs_nav') return rows.filter((r) => r.positiveFindingsCount > 0);
    if (filter === 'no_findings') return rows.filter((r) => r.positiveFindingsCount === 0);
    return rows;
  }, [rows, filter]);

  if (filtered.length === 0) {
    return (
      <p className="text-sm text-nav-slate text-center py-8">
        {t('agentF5.r1a.cl.empty')}
      </p>
    );
  }

  return (
    <div className="border-[1.5px] border-nav-card rounded-[10px] overflow-hidden divide-y divide-nav-hair bg-white">
      {filtered.map((c) => {
        const needsNav = c.positiveFindingsCount > 0;
        const safetyAlert = c.safetyConcernFlag;
        return (
          <button
            key={c.screeningId}
            type="button"
            onClick={() => onSelect(c.screeningId)}
            className={cn(
              'w-full text-left flex items-center gap-3 px-3 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
              safetyAlert
                ? 'hover:bg-nav-redTint bg-nav-redTint/40'
                : needsNav
                  ? 'hover:bg-nav-amberTint'
                  : 'hover:bg-nav-softer',
            )}
          >
            <span className="text-xs text-nav-slate tabular-nums w-20 shrink-0">
              {c.submittedAt
                ? new Date(c.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '—'}
            </span>
            <span className="font-mono text-xs text-nav-navy truncate w-24 shrink-0">…{c.cinHash.slice(-6)}</span>
            <span className="text-nav-navy tabular-nums w-20 shrink-0">
              {c.totalDurationSec != null
                ? `${Math.floor(c.totalDurationSec / 60)}m ${String(c.totalDurationSec % 60).padStart(2, '0')}s`
                : '—'}
            </span>
            {c.timeCategory && (
              <span className="font-head text-[11px] font-bold px-2 py-0.5 rounded-full bg-nav-sageTint text-nav-sageText border border-nav-sage shrink-0">
                {c.timeCategory.replace('_', '-')}
              </span>
            )}
            {needsNav && (
              <span
                className={cn(
                  'font-head text-[11px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 shrink-0',
                  safetyAlert
                    ? 'bg-nav-redTint text-nav-red border-nav-red'
                    : 'bg-nav-amberTint text-nav-amberText border-nav-amber',
                )}
                title={safetyAlert ? t('agentF5.r1a.cl.safetyFlagged') : t('agentF5.r1a.cl.hasFindings')}
              >
                <AlertTriangle className="h-3 w-3" />
                {c.positiveFindingsCount === 1
                  ? t('agentF5.r1a.cl.findingOne')
                  : t('agentF5.r1a.cl.findingMany', { n: c.positiveFindingsCount })}
              </span>
            )}
            {!needsNav && (
              <span className="font-head text-[11px] font-bold px-2 py-0.5 rounded-full bg-nav-sageTint text-nav-sageText border border-nav-sage inline-flex items-center gap-1 shrink-0">
                <CheckCircle2 className="h-3 w-3" />
                {t('agentF5.r1a.cl.clearChip')}
              </span>
            )}
            <span className="text-nav-slate text-xs ml-auto">{formatRelative(c.submittedAt)}</span>
            <ChevronRight className="h-4 w-4 text-nav-faint shrink-0" />
          </button>
        );
      })}
    </div>
  );
}

function ScreeningReviewModal({
  screeningId,
  onClose,
}: {
  screeningId: string;
  onClose: () => void;
}): JSX.Element | null {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const reviewQ = useScreeningReview(screeningId);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const r = reviewQ.data;

  return (
    <ModalShell open={true} onClose={onClose} ariaLabelledBy="review-modal-title">
      <header className="sticky top-0 bg-white border-b border-nav-hair px-6 py-5 flex items-center justify-between rounded-t-[14px] z-10">
        <div className="min-w-0">
          <h3 id="review-modal-title" className="font-head text-xl font-bold text-nav-navy truncate tracking-tight">
            {r ? `${r.client.firstName} ${r.client.lastName}` : t('agentF5.r1a.cl.loading')}
          </h3>
          {r && (
            <p className="text-xs text-nav-slate mt-0.5">
              CIN <span className="font-mono">{r.client.cin}</span> · DOB {r.client.dob} · {r.languageUsed.toUpperCase()}
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('common.close')}>
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto font-body text-nav-navy">
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="px-6 py-5 space-y-5"
        >
          {reviewQ.isLoading && (
            <div className="text-center py-8">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-nav-teal" />
            </div>
          )}

          {reviewQ.error && (
            <Alert variant="error">
              <AlertTitle>{t('agentF5.r1a.cl.errLoad')}</AlertTitle>
              <AlertDescription>{(reviewQ.error as Error).message}</AlertDescription>
            </Alert>
          )}

          {r && (
            <>
              {/* Header banner — needs nav vs no findings */}
              {r.positiveFindingsCount > 0 ? (
                <div className="rounded-[10px] bg-nav-amberTint border-[1.5px] border-nav-amber px-3 py-2.5 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-nav-amberText shrink-0" />
                  <p className="text-sm text-nav-amberText">
                    {r.positiveFindingsCount === 1
                      ? t('agentF5.r1a.cl.needsNavBanner1', {
                          domains: r.positiveDomains.map((d) => d.replace('_', ' ')).join(', '),
                        })
                      : t('agentF5.r1a.cl.needsNavBannerN', {
                          n: r.positiveFindingsCount,
                          domains: r.positiveDomains.map((d) => d.replace('_', ' ')).join(', '),
                        })}
                  </p>
                </div>
              ) : (
                <div className="rounded-[10px] bg-nav-sageTint border-[1.5px] border-nav-sage px-3 py-2.5 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-nav-sageText shrink-0" />
                  <p className="text-sm text-nav-sageText">
                    {t('agentF5.r1a.cl.noFindingsBanner')}
                  </p>
                </div>
              )}

              {/* Summary stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label={t('agentF5.r1a.cl.duration')} value={fmtDur(r.totalDurationSec)} />
                <Stat label={t('agentF5.r1a.cl.timeBucket')} value={r.timeCategory?.replace('_', '-') ?? '—'} />
                <Stat
                  label={t('agentF5.r1a.cl.navigatorQ')}
                  value={r.nextStepNavigator ? t('agentF5.r1a.cl.yes') : t('agentF5.r1a.cl.no')}
                  tone={r.nextStepNavigator ? 'amber' : 'neutral'}
                />
                <Stat
                  label="FindHelp"
                  value={r.findhelpSubmitted ? t('agentF5.r1a.cl.fhSubmitted') : t('agentF5.r1a.cl.fhPending')}
                />
              </div>

              {/* Contact */}
              <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white p-3">
                <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-teal mb-1">
                  {t('agentF5.r1a.cl.contact')}
                </p>
                {r.client.addressLine1 && (
                  <p className="text-sm text-nav-navy mb-1.5">
                    {r.client.addressLine1}
                    {r.client.city ? `, ${r.client.city}` : ''} {r.client.postalCode ?? ''}
                  </p>
                )}
                <div className="space-y-1">
                  <PhoneRow label={t('agentF5.r1a.cl.phoneCell')} number={r.client.phoneCell} />
                  <PhoneRow label={t('agentF5.r1a.cl.phoneHome')} number={r.client.phoneHome} />
                  <PhoneRow label={t('agentF5.r1a.cl.phoneOther')} number={r.client.phoneOther} />
                </div>
              </div>

              {/* Domain answers */}
              <section>
                <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-teal mb-2">
                  {t('agentF5.r1a.cl.domainAnswers')}
                </p>
                <div className="rounded-[10px] border-[1.5px] border-nav-card bg-white divide-y divide-nav-hair">
                  {r.answers.map((a) => (
                    <div key={a.domain} className="px-3 py-2.5 text-sm flex items-start gap-2">
                      {a.positiveFinding ? (
                        <AlertTriangle className="h-4 w-4 text-nav-amberText mt-0.5 shrink-0" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-nav-sageMid mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-head font-semibold text-nav-navy capitalize">
                          {a.domain.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-nav-slate mt-0.5 break-words">
                          {summarizeAnswer(a.answer)}
                        </p>
                        {a.notes && (
                          <p className="text-xs text-nav-navy mt-1 italic">"{a.notes}"</p>
                        )}
                      </div>
                      <span
                        className={cn(
                          'font-head text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 border',
                          a.positiveFinding
                            ? 'bg-nav-amberTint text-nav-amberText border-nav-amber'
                            : 'bg-nav-sageTint text-nav-sageText border-nav-sage',
                        )}
                      >
                        {a.positiveFinding ? t('agentF5.r1a.cl.positive') : t('agentF5.r1a.cl.clearChip')}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              {r.safetyTotalScore != null && r.safetyTotalScore >= 11 && (
                <div className="rounded-[10px] bg-nav-redTint border-[1.5px] border-nav-red px-3 py-2.5">
                  <p className="text-sm text-nav-red font-semibold">
                    {t('agentF5.r1a.cl.safetyScore', { n: r.safetyTotalScore })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {r && (
        <footer className="sticky bottom-0 bg-white border-t border-nav-hair px-6 py-4 flex justify-end rounded-b-[14px]">
          <Button onClick={onClose}>{t('agentF5.r1a.cl.done')}</Button>
        </footer>
      )}
    </ModalShell>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'amber' | 'neutral';
}): JSX.Element {
  const cls =
    tone === 'amber'
      ? 'border-nav-amber bg-nav-amberTint'
      : 'border-nav-card bg-white';
  return (
    <div className={cn('rounded-[10px] border-[1.5px] px-2.5 py-2', cls)}>
      <p className="font-head text-[10px] uppercase tracking-wide text-nav-slate font-bold">{label}</p>
      <p className="text-sm text-nav-navy mt-0.5">{value}</p>
    </div>
  );
}

function PhoneRow({ label, number }: { label: string; number: string | null }): JSX.Element | null {
  if (!number) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-xs text-nav-slate w-12">{label}</span>
      <a
        href={`tel:${number}`}
        className="inline-flex items-center gap-1 text-nav-teal hover:text-nav-tealDark underline-offset-2 hover:underline"
      >
        <Phone className="h-3 w-3" />
        {formatPhone(number)}
      </a>
    </div>
  );
}

function fmtDur(sec: number | null): string {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function summarizeAnswer(a: Record<string, unknown>): string {
  if ('answer' in a) return String(a.answer);
  if ('selections' in a && Array.isArray(a.selections)) {
    return a.selections.length > 0 ? a.selections.join(', ') : '—';
  }
  // Compose key:value list for multi-field domains (food, safety, physical_ability)
  return Object.entries(a)
    .filter(([k]) => k !== 'total_score')
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ');
}
